import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  IconButton,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteIcon from '@mui/icons-material/Delete';
import documentService, { SUPPORTED_FILE_TYPES } from '../../services/documentService';

interface FileUploadProps {
  onUploadSuccess?: (response: any) => void;
  onUploadError?: (error: any) => void;
}

const FileUpload: React.FC<FileUploadProps> = ({
  onUploadSuccess,
  onUploadError
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [unsupportedFiles, setUnsupportedFiles] = useState<string[]>([]);
  const [numQuestions, setNumQuestions] = useState<number>(10);
  const [difficulty, setDifficulty] = useState<string>('medium');
  const [language, setLanguage] = useState<string>('vietnamese');
  const [useCustomTypes, setUseCustomTypes] = useState<boolean>(false);
  const [questionTypes, setQuestionTypes] = useState({
    multiple_choice: 10,
    true_false: 5,
    fill_in_blank: 5
  });
  const [processingStatus, setProcessingStatus] = useState<string>('');
  const [currentDocument, setCurrentDocument] = useState<any>(null);
  const [questionsCount, setQuestionsCount] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Helper function để xử lý lỗi validation từ backend
  const handleValidationError = (err: any): string => {
    if (err.validation_failed && err.errors) {
      // Tạo thông báo lỗi chi tiết từ validation errors
      const validationErrors = Object.entries(err.errors).map(([field, errorData]: [string, any]) => {
        if (typeof errorData === 'object' && errorData.details) {
          return errorData.details;
        }
        return errorData;
      }).join('\n');

      return validationErrors;
    } else {
      return err.message || 'Không thể tải tệp lên. Vui lòng thử lại.';
    }
  };

  // Constants
  const MAX_TOTAL_SIZE = 2 * 1024 * 1024; // 2MB in bytes
  const MAX_FILES = 10; // Maximum number of files allowed

  // Difficulty options
  const difficultyOptions = [
    { value: 'easy', label: 'Dễ (Easy)' },
    { value: 'medium', label: 'Trung bình (Medium)' },
    { value: 'hard', label: 'Khó (Hard)' }
  ];

  // Language options
  const languageOptions = [
    { value: 'vietnamese', label: 'Tiếng Việt' },
    { value: 'english', label: 'Tiếng Anh' },
    { value: 'chinese', label: 'Tiếng Trung Quốc' }
  ];

  // Calculate total size of selected files
  const getTotalSize = (): number => {
    return selectedFiles.reduce((total, file) => total + file.size, 0);
  };

  // Get total size in readable format
  const getTotalSizeFormatted = (): string => {
    const totalBytes = getTotalSize();
    if (totalBytes < 1024) {
      return `${totalBytes} B`;
    } else if (totalBytes < 1024 * 1024) {
      return `${(totalBytes / 1024).toFixed(1)} KB`;
    } else {
      return `${(totalBytes / (1024 * 1024)).toFixed(1)} MB`;
    }
  };

  // Check if total size exceeds limit
  const isTotalSizeExceeded = (): boolean => {
    return getTotalSize() > MAX_TOTAL_SIZE;
  };

  // Generate random distribution for question types
  const generateRandomDistribution = (totalQuestions: number) => {
    // Phân bố theo tỷ lệ cố định: 60% multiple choice, 20% true/false, 20% fill in blank
    let multipleChoice = Math.round(totalQuestions * 0.6); // 60% cho multiple choice
    let trueFalse = Math.round(totalQuestions * 0.2); // 20% cho true/false
    let fillInBlank = totalQuestions - multipleChoice - trueFalse; // Phần còn lại cho fill in blank

    // Đảm bảo mỗi loại có ít nhất 1 câu nếu tổng số >= 3
    if (totalQuestions >= 3) {
      if (multipleChoice < 1) multipleChoice = 1;
      if (trueFalse < 1) trueFalse = 1;
      if (fillInBlank < 1) fillInBlank = 1;

      // Điều chỉnh lại nếu tổng vượt quá
      while (multipleChoice + trueFalse + fillInBlank > totalQuestions) {
        if (fillInBlank > 1) fillInBlank--;
        else if (trueFalse > 1) trueFalse--;
        else if (multipleChoice > 1) multipleChoice--;
      }
    } else if (totalQuestions === 2) {
      // Nếu có 2 câu, ưu tiên multiple choice và true/false
      multipleChoice = 1;
      trueFalse = 1;
      fillInBlank = 0;
    } else if (totalQuestions === 1) {
      // Nếu chỉ có 1 câu, dùng multiple choice
      multipleChoice = 1;
      trueFalse = 0;
      fillInBlank = 0;
    }

    return {
      multiple_choice: multipleChoice,
      true_false: trueFalse,
      fill_in_blank: fillInBlank
    };
  };

  // Tự động cập nhật tổng số câu hỏi khi thay đổi số lượng từng loại
  const updateQuestionType = (type: keyof typeof questionTypes, value: string) => {
    const parsed = parseInt(value);
    const nextValue = value === '' ? 0 : Math.max(0, isNaN(parsed) ? 0 : parsed);
    const newQuestionTypes = {
      ...questionTypes,
      [type]: nextValue
    } as typeof questionTypes;

    if (useCustomTypes) {
      const total = Object.values(newQuestionTypes).reduce((sum, val) => sum + val, 0);
      if (total > 30) {
        setError('Tối đa 30 câu hỏi.');
        return;
      }
      if (error) setError(null);
      setQuestionTypes(newQuestionTypes);
      setNumQuestions(total);
      return;
    }

    setQuestionTypes(newQuestionTypes);
  };

  // Handle checkbox change
  const handleCustomTypesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUseCustomTypes(e.target.checked);
    if (e.target.checked) {
      // Khi bật tùy chỉnh, cập nhật tổng số câu hỏi theo sum của các loại
      const total = Object.values(questionTypes).reduce((sum, val) => sum + val, 0);
      setNumQuestions(total);
    }
  };

  // Handle number of questions change
  const handleNumQuestionsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const parsed = parseInt(value);
    let numValue = value === '' ? 0 : Math.max(1, isNaN(parsed) ? 0 : parsed);
    if (numValue > 30) {
      numValue = 30;
      setError('Tối đa 30 câu hỏi.');
    } else if (error) {
      // Clear previous error if any when input becomes valid
      setError(null);
    }
    setNumQuestions(numValue);

    // Tự động tắt checkbox tùy chỉnh khi thay đổi tổng số câu hỏi
    if (useCustomTypes) {
      setUseCustomTypes(false);
    }

    // Cập nhật phân bố câu hỏi ngẫu nhiên
    if (!useCustomTypes) {
      setQuestionTypes(generateRandomDistribution(numValue));
    }
  };

  // Hàm chuẩn hóa tên file
  const normalizeFileName = (fileName: string): string => {
    // Lấy phần mở rộng file
    const fileExtension = fileName.substring(fileName.lastIndexOf('.'));

    // Lấy tên file không có phần mở rộng
    const nameWithoutExtension = fileName.substring(0, fileName.lastIndexOf('.'));

    // Chuẩn hóa tên file:
    // 1. Chuyển về chữ thường
    // 2. Bỏ dấu tiếng Việt
    // 3. Thay thế các ký tự đặc biệt bằng dấu gạch ngang
    // 4. Bỏ khoảng trắng đầu/cuối
    // 5. Thay khoảng trắng bằng dấu gạch ngang
    const normalizedName = nameWithoutExtension
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');

    // Trả về tên file đã chuẩn hóa + phần mở rộng
    return `${normalizedName}${fileExtension.toLowerCase()}`;
  };

  // Handle file selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      return;
    }

    const newFiles = Array.from(files);

    // Check if adding these files would exceed the maximum number
    if (selectedFiles.length + newFiles.length > MAX_FILES) {
      setError(`Chỉ được phép chọn tối đa ${MAX_FILES} file.`);
      return;
    }

    // Validate each file
    const validFiles: File[] = [];
    let totalSize = getTotalSize();
    const unsupported: string[] = [];

    for (const file of newFiles) {
      // Validate file type
      if (!documentService.validateFileType(file)) {
        unsupported.push(file.name);
        continue;
      }

      // Check if adding this file would exceed total size limit
      if (totalSize + file.size > MAX_TOTAL_SIZE) {
        setError(`File "${file.name}" sẽ làm tổng dung lượng vượt quá 2MB. Tổng hiện tại: ${getTotalSizeFormatted()}`);
        continue;
      }

      // Create normalized file
      const normalizedFile = new File(
        [file],
        normalizeFileName(file.name),
        { type: file.type }
      );

      validFiles.push(normalizedFile);
      totalSize += file.size;
    }

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
      setError(null);
    }
    if (unsupported.length > 0) {
      setUnsupportedFiles(prev => [...prev, ...unsupported]);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove a specific file
  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    // Clear unsupported files when no files are selected
    if (selectedFiles.length === 1) {
      setUnsupportedFiles([]);
    }
  };

  // Remove all files
  const clearAllFiles = () => {
    setSelectedFiles([]);
    setUnsupportedFiles([]);
    setError(null);
  };

  // Get status message in Vietnamese
  const getStatusMessage = (status: string): string => {
    switch (status) {
      case 'uploading':
        return 'Đang upload file lên server...';
      case 'pending_processing':
        return 'Đang xử lý tài liệu bằng AI...';
      case 'processing':
        return 'Đang tạo câu hỏi từ nội dung...';
      case 'completed':
        return `Hoàn thành! Đã tạo ${questionsCount} câu hỏi`;
      case 'upload_failed':
        return 'Upload file thất bại';
      case 'processing_failed':
        return currentDocument?.error_message ?
          `Xử lý thất bại: ${currentDocument.error_message}` :
          'Xử lý tài liệu thất bại';
      default:
        return 'Đang khởi tạo...';
    }
  };

  // Get status progress percentage
  const getStatusProgress = (status: string): number => {
    switch (status) {
      case 'uploading':
        return 25;
      case 'pending_processing':
        return 50;
      case 'processing':
        return 75;
      case 'completed':
        return 100;
      case 'upload_failed':
      case 'processing_failed':
        return 0;
      default:
        return 10;
    }
  };

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon />;
      case 'upload_failed':
      case 'processing_failed':
        return <ErrorIcon />;
      default:
        return <CircularProgress size={16} color="inherit" />;
    }
  };

  // Handle file upload
  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError('Vui lòng chọn ít nhất một tệp trước.');
      return;
    }

    // Validate total size
    if (isTotalSizeExceeded()) {
      setError('Tổng dung lượng file vượt quá 2MB. Vui lòng chọn file nhỏ hơn.');
      return;
    }

    // Validate number of questions
    if (useCustomTypes) {
      const totalQuestions = questionTypes.multiple_choice + questionTypes.true_false + questionTypes.fill_in_blank;
      if (totalQuestions === 0) {
        setError('Phải có ít nhất 1 câu hỏi.');
        return;
      }
      if (totalQuestions > 30) {
        setError('Tối đa 30 câu hỏi.');
        return;
      }
    }
    if (!useCustomTypes) {
      if (numQuestions < 1) {
        setError('Phải có ít nhất 1 câu hỏi.');
        return;
      }
      if (numQuestions > 30) {
        setError('Tối đa 30 câu hỏi.');
        return;
      }
    }

    setUploading(true);
    setError(null);
    setProcessingStatus('uploading');

    try {
      // Prepare upload options
      const uploadOptions: any = {
        difficulty,
        language,
        files: selectedFiles // Pass all files
      };

      if (useCustomTypes) {
        uploadOptions.question_types = questionTypes;
        uploadOptions.numQuestions = questionTypes.multiple_choice + questionTypes.true_false + questionTypes.fill_in_blank;
      } else {
        // Generate random distribution for auto mode
        const randomDistribution = generateRandomDistribution(numQuestions);
        uploadOptions.question_types = randomDistribution;
        uploadOptions.numQuestions = numQuestions;
      }

      // Upload files using the updated service
      const response = await documentService.uploadMultipleDocuments(selectedFiles, uploadOptions);

      if (response.success && response.document) {
        setCurrentDocument(response.document);
        setProcessingStatus(response.document.processing_status || 'uploading');

        // Start polling for status updates
        const originalDocumentId = response.document.id;
        documentService.pollUploadStatus(
          originalDocumentId,
          (status: string, data: any) => {
            console.log('Polling status update:', status, data);
            setProcessingStatus(status);

            // Find the current document from the response
            let currentDoc = null;
            if (data.documents && Array.isArray(data.documents)) {
              // If response contains documents array, find by ID
              currentDoc = data.documents.find((doc: any) => doc.id === originalDocumentId);
            } else if (data.document) {
              // If response contains single document
              currentDoc = data.document;
            } else if (data.document_id === originalDocumentId) {
              // If response is the document itself
              currentDoc = data;
            }

            if (currentDoc) {
              setCurrentDocument(currentDoc);
              setQuestionsCount(currentDoc.questions_count || 0);

              // If processing failed, stop polling and show error
              if (currentDoc.processing_status === 'processing_failed' || currentDoc.processing_status === 'upload_failed') {
                // Nếu là lỗi vĩnh viễn, mới dừng polling
                const errorMsg = currentDoc.error_message ?
                  `Xử lý thất bại: ${currentDoc.error_message}` :
                  'Xử lý tài liệu thất bại. Vui lòng thử lại.';
                setError(errorMsg);
                setUploading(false);
                setSelectedFiles([]);
                setUnsupportedFiles([]); // Clear unsupported files on failure

                // Reset file input
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }

                if (onUploadError) {
                  onUploadError({ message: currentDoc.error_message || 'Xử lý thất bại' });
                }
                return; // Stop polling
              }
            }
          },
          Infinity, // Wait indefinitely
          2000 // interval (2 seconds)
        ).then((finalResult) => {
          // Processing completed
          console.log('Final polling result:', finalResult);
          setUploading(false);

          // Find the current document from the final result
          let finalDoc = null;
          if (finalResult.documents && Array.isArray(finalResult.documents)) {
            // If response contains documents array, find by ID
            finalDoc = finalResult.documents.find((doc: any) => doc.id === originalDocumentId);
          } else if (finalResult.document) {
            // If response contains single document
            finalDoc = finalResult.document;
          } else if (finalResult.document_id === originalDocumentId) {
            // If response is the document itself
            finalDoc = finalResult;
          }

          if (finalDoc) {
            setProcessingStatus(finalDoc.processing_status);
            setQuestionsCount(finalDoc.questions_count || 0);

            if (finalDoc.processing_status === 'completed') {
              setSuccess(`Upload và xử lý hoàn thành! Đã tạo ${finalDoc.questions_count || 0} câu hỏi.`);
              setSelectedFiles([]);
              setUnsupportedFiles([]); // Clear unsupported files on success

              // Reset file input
              if (fileInputRef.current) {
                fileInputRef.current.value = '';
              }

              // Call the success callback if provided
              if (onUploadSuccess) {
                onUploadSuccess({
                  ...finalResult,
                  document: finalDoc,
                  document_id: originalDocumentId
                });
              }
            } else if (finalDoc.processing_status === 'processing_failed' || finalDoc.processing_status === 'upload_failed') {
              // Show error message with details
              const errorMsg = finalDoc.error_message ?
                `Xử lý thất bại: ${finalDoc.error_message}` :
                'Xử lý tài liệu thất bại. Vui lòng thử lại.';
              setError(errorMsg);
              setSelectedFiles([]);
              setUnsupportedFiles([]); // Clear unsupported files on failure

              // Reset file input
              if (fileInputRef.current) {
                fileInputRef.current.value = '';
              }

              if (onUploadError) {
                onUploadError({ message: finalDoc.error_message || 'Xử lý thất bại' });
              }
            } else {
              setError('Xử lý tài liệu thất bại. Vui lòng thử lại.');
              if (onUploadError) {
                onUploadError({ message: 'Xử lý thất bại' });
              }
            }
          } else {
            setError('Không tìm thấy thông tin document sau khi xử lý.');
            if (onUploadError) {
              onUploadError({ message: 'Không tìm thấy tài liệu trong kết quả cuối cùng' });
            }
          }
        }).catch((err) => {
          setUploading(false);
          setError(handleValidationError(err));

          if (onUploadError) {
            onUploadError(err);
          }
        });
      } else {
        // Kiểm tra xem có phải lỗi validation không
        if (response.validation_failed && response.errors) {
          throw new Error(handleValidationError(response));
        } else {
          throw new Error(response.message || 'Tải lên thất bại');
        }
      }
    } catch (err: any) {
      setUploading(false);
      setProcessingStatus('');
      setError(handleValidationError(err));

      setSelectedFiles([]);
      setUnsupportedFiles([]); // Clear unsupported files on error

      // Call the error callback if provided
      if (onUploadError) {
        onUploadError(err);
      }
    }
  };

  // Handle drag over event
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  // Handle drop event
  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();

    const files = event.dataTransfer.files;
    if (!files || files.length === 0) {
      return;
    }

    const newFiles = Array.from(files);

    // Check if adding these files would exceed the maximum number
    if (selectedFiles.length + newFiles.length > MAX_FILES) {
      setError(`Chỉ được phép chọn tối đa ${MAX_FILES} file.`);
      return;
    }

    // Validate each file
    const validFiles: File[] = [];
    let totalSize = getTotalSize();
    const unsupported: string[] = [];

    for (const file of newFiles) {
      // Validate file type
      if (!documentService.validateFileType(file)) {
        unsupported.push(file.name);
        continue;
      }

      // Check if adding this file would exceed total size limit
      if (totalSize + file.size > MAX_TOTAL_SIZE) {
        setError(`File "${file.name}" sẽ làm tổng dung lượng vượt quá 2MB. Tổng hiện tại: ${getTotalSizeFormatted()}`);
        continue;
      }

      // Create normalized file
      const normalizedFile = new File(
        [file],
        normalizeFileName(file.name),
        { type: file.type }
      );

      validFiles.push(normalizedFile);
      totalSize += file.size;
    }

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
      setError(null);
    }
    if (unsupported.length > 0) {
      setUnsupportedFiles(prev => [...prev, ...unsupported]);
    }
  };

  // Handle click on upload area
  const handleUploadAreaClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle close snackbar
  const handleCloseSnackbar = () => {
    setSuccess(null);
  };

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      {/* Configuration Options */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Cấu hình tạo câu hỏi
          </Typography>

          <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                type="number"
                label="Số lượng câu hỏi"
                value={numQuestions || ''}
                onChange={handleNumQuestionsChange}
                inputProps={{ min: 1, max: 30 }}
                helperText="Tối thiểu 1, tối đa 30 câu hỏi"
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <FormControl fullWidth>
                <InputLabel id="difficulty-label">Độ khó</InputLabel>
                <Select
                  labelId="difficulty-label"
                  value={difficulty}
                  label="Độ khó"
                  onChange={(e) => setDifficulty(e.target.value)}
                  sx={{ textAlign: 'left' }}
                >
                  {difficultyOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box>
                        <Typography variant="body1">
                          {option.label}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ flex: 1 }}>
              <FormControl fullWidth>
                <InputLabel id="language-label">Ngôn ngữ</InputLabel>
                <Select
                  labelId="language-label"
                  value={language}
                  label="Ngôn ngữ"
                  onChange={(e) => setLanguage(e.target.value)}
                  sx={{ textAlign: 'left' }}
                >
                  {languageOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box>
                        <Typography variant="body1">
                          {option.label}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Question Types Configuration */}
          <Box sx={{ mt: 3 }}>
            <FormControl component="fieldset" sx={{ width: '100%' }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2
              }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={useCustomTypes}
                      onChange={handleCustomTypesChange}
                    />
                  }
                  label="Tùy chỉnh số lượng từng loại câu hỏi"
                />
              </Box>

              {useCustomTypes && (
                <Box sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 2,
                  mt: 2
                }}>
                  <TextField
                    type="number"
                    label="Trắc nghiệm (Multiple Choice)"
                    value={questionTypes.multiple_choice || ''}
                    onChange={(e) => updateQuestionType('multiple_choice', e.target.value)}
                    inputProps={{ min: 0 }}
                    helperText="4 đáp án A, B, C, D"
                    size="small"
                    sx={{ minWidth: 200 }}
                  />

                  <TextField
                    type="number"
                    label="Đúng/Sai (True/False)"
                    value={questionTypes.true_false || ''}
                    onChange={(e) => updateQuestionType('true_false', e.target.value)}
                    inputProps={{ min: 0 }}
                    helperText="Chọn Đúng hoặc Sai"
                    size="small"
                    sx={{ minWidth: 200 }}
                  />

                  <TextField
                    type="number"
                    label="Điền khuyết (Fill in Blank)"
                    value={questionTypes.fill_in_blank || ''}
                    onChange={(e) => updateQuestionType('fill_in_blank', e.target.value)}
                    inputProps={{ min: 0 }}
                    helperText="Điền từ vào chỗ trống"
                    size="small"
                    sx={{ minWidth: 200 }}
                  />
                </Box>
              )}

              {useCustomTypes && (
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                  Tổng: {questionTypes.multiple_choice + questionTypes.true_false + questionTypes.fill_in_blank} câu hỏi
                </Typography>
              )}

              {!useCustomTypes && (
                <Typography variant="caption" color="textSecondary">
                  Hệ thống sẽ tự động tạo mix các loại câu hỏi: Trắc nghiệm, Đúng/Sai, Điền vào chỗ trống
                </Typography>
              )}
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Hidden file input */}
      <input
        type="file"
        multiple
        accept={SUPPORTED_FILE_TYPES.join(',')}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        ref={fileInputRef}
      />

      {/* Drag and drop area */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: '#f5f5f5',
          border: '2px dashed #9e9e9e',
          '&:hover': {
            backgroundColor: '#eeeeee',
          },
          mb: 2
        }}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleUploadAreaClick}
      >
        <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
        <Typography variant="h6" gutterBottom>
          Kéo & Thả tài liệu vào đây
        </Typography>
        <Typography variant="body2" color="textSecondary">
          hoặc nhấp để duyệt tài liệu
        </Typography>
        <Typography variant="caption" display="block" color="textSecondary" sx={{ mt: 1 }}>
          Các định dạng được hỗ trợ: PDF, Word (.doc, .docx), Văn bản (.txt), Hình ảnh (.jpeg, .png, .tiff, .bmp, .webp)
        </Typography>
        <Typography variant="caption" display="block" color="textSecondary" sx={{ mt: 1 }}>
          Tối đa {MAX_FILES} file, tổng dung lượng ≤ 2MB
        </Typography>
        {unsupportedFiles.length > 0 && (
          <Box sx={{ mt: 2, p: 2, borderRadius: 1, border: '1px solid', borderColor: 'warning.main' }}>
            <Typography variant="caption" color="warning.dark" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
              ⚠️ Có {unsupportedFiles.length} file không được hỗ trợ
            </Typography>
            <Typography variant="caption" color="warning.dark">
              Các file này sẽ không được xử lý. Vui lòng kiểm tra định dạng file.
            </Typography>
          </Box>
        )}
      </Paper>

      {/* File size info */}
      {selectedFiles.length > 0 && (
        <Box sx={{ mb: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="textSecondary">
              Đã chọn {selectedFiles.length} file
            </Typography>
            <Typography variant="body2" color={isTotalSizeExceeded() ? 'error.main' : 'textSecondary'}>
              Tổng dung lượng: {getTotalSizeFormatted()}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={(getTotalSize() / MAX_TOTAL_SIZE) * 100}
            color={isTotalSizeExceeded() ? 'error' : 'primary'}
            sx={{ height: 6, borderRadius: 3 }}
          />
          {isTotalSizeExceeded() && (
            <Typography variant="caption" color="error.main" sx={{ mt: 1, display: 'block' }}>
              ⚠️ Tổng dung lượng vượt quá 2MB. Vui lòng bớt file hoặc chọn file nhỏ hơn.
            </Typography>
          )}
        </Box>
      )}

      {/* Selected files list */}
      {selectedFiles.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Danh sách file đã chọn
              </Typography>
              <Button
                size="small"
                color="error"
                onClick={clearAllFiles}
                disabled={uploading}
              >
                Xóa tất cả
              </Button>
            </Box>

            <List dense>
              {selectedFiles.map((file, index) => (
                <ListItem key={index} sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 1, mb: 1 }}>
                  <ListItemText
                    primary={file.name}
                    secondary={`${(file.size / 1024).toFixed(1)} KB`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => removeFile(index)}
                      disabled={uploading}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Unsupported files list */}
      {unsupportedFiles.length > 0 && (
        <Card sx={{ mb: 2, border: '1px solid', borderColor: 'warning.main' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ErrorIcon color="warning" />
                <Typography variant="h6" color="warning.dark">
                  File không được hỗ trợ ({unsupportedFiles.length})
                </Typography>
              </Box>
              <Button
                size="small"
                color="warning"
                variant="outlined"
                onClick={() => setUnsupportedFiles([])}
                disabled={uploading}
                startIcon={<DeleteIcon />}
              >
                Xóa tất cả
              </Button>
            </Box>

            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Các file sau đây có định dạng không được hỗ trợ và sẽ không được xử lý:
            </Typography>

            <List dense>
              {unsupportedFiles.map((fileName, index) => (
                <ListItem key={index} sx={{
                  border: '1px solid',
                  borderColor: 'warning.light',
                  borderRadius: 1,
                  mb: 1
                }}>
                  <ListItemText
                    primary={fileName}
                    secondary="Định dạng không được hỗ trợ"
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: 'medium',
                      color: 'warning.dark'
                    }}
                    secondaryTypographyProps={{
                      variant: 'caption',
                      color: 'warning.dark'
                    }}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => setUnsupportedFiles(prev => prev.filter(f => f !== fileName))}
                      disabled={uploading}
                      color="warning"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>

            <Box sx={{ mt: 2, p: 2, borderRadius: 1, border: '1px solid', borderColor: 'info.main' }}>
              <Typography variant="caption" color="info.dark" sx={{ fontWeight: 'medium', display: 'block', mb: 1 }}>
                💡 Gợi ý:
              </Typography>
              <Typography variant="caption" color="info.dark">
                Chuyển đổi file sang định dạng được hỗ trợ: PDF, Word (.doc, .docx), Văn bản (.txt), hoặc Hình ảnh (.jpeg, .png, .tiff, .bmp, .webp)
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Upload button */}
      {selectedFiles.length > 0 && !isTotalSizeExceeded() && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleUpload}
            disabled={uploading}
            startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
          >
            {uploading ? 'Đang tải lên...' : `Tải lên ${selectedFiles.length} file`}
          </Button>
        </Box>
      )}

      {/* Processing Status Popup */}
      {(uploading && !!processingStatus) && (
        <Card
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            m: 0,
            maxWidth: 400,
            width: 380,
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            zIndex: 1400,
            p: 0
          }}
        >
          <Box
            sx={{
              pb: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
              px: 2,
              pt: 1.5
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AutoAwesomeIcon />
              <Typography variant="h6">
                Xử lý tài liệu
              </Typography>
            </Box>
            {(processingStatus === 'completed' || processingStatus === 'processing_failed' || processingStatus === 'upload_failed') && (
              <IconButton
                size="small"
                onClick={() => setUploading(false)}
                sx={{ color: 'white' }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </Box>
          <CardContent sx={{ pt: 2, pb: 2 }}>
            <Box sx={{ mb: 2, mt: 2 }}>
              <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                {getStatusMessage(processingStatus)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={getStatusProgress(processingStatus)}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    flexGrow: 1,
                    mr: 2,
                    backgroundColor: 'grey.200',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      backgroundColor:
                        processingStatus === 'completed' ? 'success.main' :
                          processingStatus === 'upload_failed' || processingStatus === 'processing_failed' ? 'error.main' :
                            'primary.main'
                    }
                  }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" color="textSecondary">
                    {getStatusProgress(processingStatus)}%
                  </Typography>
                  <Chip
                    size="small"
                    label={
                      processingStatus === 'completed' ? 'Hoàn thành' :
                        processingStatus === 'upload_failed' || processingStatus === 'processing_failed' ? 'Thất bại' :
                          processingStatus === 'uploading' ? 'Đang tải lên' :
                            processingStatus === 'pending_processing' ? 'Xử lý AI' :
                              processingStatus === 'processing' ? 'Tạo câu hỏi' :
                                'Đang xử lý'
                    }
                    color={
                      processingStatus === 'completed' ? 'success' :
                        processingStatus === 'upload_failed' || processingStatus === 'processing_failed' ? 'error' :
                          'primary'
                    }
                    icon={getStatusIcon(processingStatus)}
                  />
                </Box>
              </Box>
            </Box>
            {currentDocument && (
              <Box sx={{
                p: 2,
                backgroundColor: 'grey.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200',
                mb: 2
              }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  <strong>📄 Tài liệu:</strong> {currentDocument.name}
                </Typography>
                {questionsCount > 0 && (
                  <Typography variant="body2" color="success.main">
                    <strong>✅ Câu hỏi đã tạo:</strong> {questionsCount}
                  </Typography>
                )}
              </Box>
            )}
            {processingStatus === 'completed' && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="success.main" sx={{ fontWeight: 'bold' }}>
                  🎉 Xử lý hoàn thành! Bạn sẽ được chuyển đến trang xem câu hỏi...
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {/* Success message */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FileUpload; 